import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Textarea } from "@/modules/shared/ui/components/textarea"
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/modules/shared/ui/components/tooltip"
import { Sparkles, Loader2, Info, AlertTriangle } from "lucide-react"
import { Alert, AlertTitle, AlertDescription } from "@/modules/shared/ui/components/alert"
import { useLookupService } from "@/modules/shared/infrastructure/lookup-service"
import { useQuery } from "@tanstack/react-query"

interface AIVacancyDialogProps {
    isOpen: boolean
    setIsOpen: (open: boolean) => void
    onGenerate: (prompt: string) => Promise<void>
}

export const AIVacancyDialog = ({ isOpen, setIsOpen, onGenerate }: AIVacancyDialogProps) => {
    const [prompt, setPrompt] = useState("")
    const [isGenerating, setIsGenerating] = useState(false)
    const [warningMessage, setWarningMessage] = useState<string | null>(null)
    const lookupService = useLookupService();

    const { data: promptLengthLookup } = useQuery<{ id: string; code: string; name: string } | null>({
        queryKey: ["vacancy-prompt-length"],
        queryFn: () => lookupService.getAllowedVacancyPromptLength(),
        staleTime: 60_000,
        retry: false,
        refetchOnWindowFocus: false,
    })

    const maxPromptLength = parseInt(promptLengthLookup?.code ?? "1000")

    const handleGenerate = async (): Promise<void> => {
      if (!prompt.trim()) return

      setWarningMessage(null)
      setIsGenerating(true)
      try {
        await onGenerate(prompt)
        setPrompt("")
        setIsOpen(false)
      } catch (error: unknown) {
        if (error instanceof Error && error.name === "InsufficientFundsError") {
          setIsOpen(false)
          return
        }
        const message =
          error instanceof Error
            ? error.message
            : "The input does not represent a legitimate job role"
        setWarningMessage(message)
      } finally {
        setIsGenerating(false)
      }
    }

    return (
      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open)
          if (!open) {
            setWarningMessage(null)
            setPrompt("")
          }
        }}
      >
        <DialogContent className="sm:max-w-[600px] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              Create Vacancy with AI
            </DialogTitle>
            <DialogDescription>
              Describe the vacancy you want to create, and our AI will generate
              a comprehensive job posting for you.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <label
                  htmlFor="prompt"
                  className="text-sm font-medium text-foreground"
                >
                  Vacancy Description Prompt
                </label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent
                    side="top"
                    className="max-w-[260px] p-0 overflow-hidden"
                  >
                    <div className="px-3.5 py-2.5 space-y-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-3.5 h-3.5 shrink-0 text-primary" />
                        <span className="text-[13px] font-semibold text-white leading-tight">
                          Vacancy Description Prompt
                        </span>
                      </div>
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        Describe the role in plain language. Include the job
                        title, required experience, key skills,
                        responsibilities, and any compensation or location
                        details for the best results.
                      </p>
                    </div>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Textarea
                id="prompt"
                placeholder="Example: Senior software engineer with 5+ years experience in React and Node.js, responsible for leading frontend development and mentoring junior developers. Salary range 80k-120k, remote position..."
                value={prompt}
                onChange={(e) => {
                  setPrompt(e.target.value)
                  setWarningMessage(null)
                }}
                className={`min-h-[200px] resize-none field-sizing-fixed ${prompt.length > maxPromptLength ? "border-destructive focus-visible:ring-destructive" : ""}`}
                disabled={isGenerating}
              />
              <div className="flex justify-between items-center">
                {prompt.length > maxPromptLength ? (
                  <p className="text-xs text-destructive">
                    Prompt exceeds maximum length
                  </p>
                ) : (
                  <span />
                )}
                <p
                  className={`text-xs ${prompt.length > maxPromptLength ? "text-destructive" : "text-muted-foreground"}`}
                >
                  {prompt.length}/{maxPromptLength}
                </p>
              </div>
            </div>
          </div>

          {warningMessage &&
            (() => {
              const isPolicyViolation =
                /legitimate job role|content policy/i.test(warningMessage)
              const title = isPolicyViolation
                ? "Content Policy Violation"
                : "Unable to generate vacancy"
              return (
                <Alert className="border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  <AlertTitle className="text-amber-800 dark:text-amber-300">
                    {title}
                  </AlertTitle>
                  <AlertDescription className="text-amber-700 dark:text-amber-400">
                    {warningMessage}
                  </AlertDescription>
                </Alert>
              )
            })()}

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setIsOpen(false)
                setPrompt("")
              }}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={
                !prompt.trim() ||
                isGenerating ||
                prompt.length > maxPromptLength
              }
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Generate Vacancy
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
}
